import {
  Button,
  Group,
  MultiSelect,
  Pagination,
  Paper,
  Stack,
  Table,
  Tabs,
  TextInput,
  Title,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { PiDownloadSimple } from 'react-icons/pi';
import { useLocation } from 'wouter';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { urlField } from '@/lib/urlState';
import {
  useLeadsList,
  LEAD_SOURCES,
  LEAD_STATUSES,
  type LeadListFilters,
  type LeadSource,
  type LeadStatus,
} from '@/models/leads';
import { useHasAnyPermission } from '@/models/permissions';
import { formatDate } from '@/lib/helpers';
import { OwnerFilter } from '@/components/OwnerFilter/OwnerFilter';
import { AssignAssigneeControl } from './AssignAssigneeControl';
import { CreateLeadModal } from './CreateLeadModal';
import { ImportMondayLeadModal } from './ImportMondayLeadModal';
import { StatusBadge } from './StatusBadge';

const STATUSES: LeadStatus[] = LEAD_STATUSES;

export default function LeadListPage() {
  const canReadAll = useHasAnyPermission(['leads:read-all', 'leads:*', '*:*']);
  const canAssign = useHasAnyPermission(['leads:assign', 'leads:*', '*:*']);
  const canWrite = useHasAnyPermission(['leads:write', 'leads:*', '*:*']);

  const schema = useMemo(
    () => ({
      view: canReadAll
        ? urlField.enum(['mine', 'unassigned', 'all'] as const, 'all')
        : urlField.enum(['mine'] as const, 'mine'),
      search: urlField.string(),
      owners: urlField.numberArray(),
      status: urlField.stringArray(LEAD_STATUSES),
      source: urlField.stringArray(LEAD_SOURCES),
      page: urlField.page(),
      pageSize: urlField.number(50, { integer: true, min: 1, max: 200 }),
      sort: urlField.enum(
        ['createdAt', 'updatedAt', 'lastContactAt'] as const,
        'createdAt',
      ),
      order: urlField.enum(['asc', 'desc'] as const, 'desc'),
    }),
    [canReadAll],
  );
  const [state, setState, { clear, isFiltered }] = useUrlState(schema);
  const [searchInput, setSearchInput] = useUrlDraft(state.search, (search) =>
    setState({ search }),
  );
  const filters: LeadListFilters = {
    view: state.view,
    search: state.search || undefined,
    ownerUserId: state.owners.length ? state.owners : undefined,
    status: state.status.length ? state.status : undefined,
    source: state.source.length ? state.source : undefined,
    page: state.page,
    pageSize: state.pageSize,
    sort: state.sort,
    order: state.order,
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { data, isLoading } = useLeadsList(filters);
  const [, navigate] = useLocation();

  return (
    <Paper p="md" h="100%">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Leads</Title>
          {canWrite && (
            <Group gap="xs">
              <Button
                variant="default"
                leftSection={<PiDownloadSimple />}
                onClick={() => setImportOpen(true)}
              >
                Import from Monday
              </Button>
              <Button onClick={() => setCreateOpen(true)}>New lead</Button>
            </Group>
          )}
        </Group>

        <Tabs
          value={state.view}
          onChange={(v) => v && setState({ view: v as typeof state.view })}
        >
          <Tabs.List>
            <Tabs.Tab value="mine">My open leads</Tabs.Tab>
            {canReadAll && <Tabs.Tab value="unassigned">Unassigned</Tabs.Tab>}
            {canReadAll && <Tabs.Tab value="all">All</Tabs.Tab>}
          </Tabs.List>
        </Tabs>

        <Group>
          <TextInput
            placeholder="Search name/email/phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            w={280}
          />
          <OwnerFilter
            size="sm"
            value={state.owners}
            onChange={(owners) => setState({ owners })}
            w={220}
          />
          <MultiSelect
            placeholder="Status"
            data={STATUSES}
            value={state.status}
            onChange={(v) => setState({ status: v as LeadStatus[] })}
            w={220}
          />
          <MultiSelect
            placeholder="Source"
            data={LEAD_SOURCES}
            searchable
            value={state.source}
            onChange={(v) => setState({ source: v as LeadSource[] })}
            w={220}
          />
          <ClearFiltersButton visible={isFiltered} onClick={clear} />
        </Group>

        <Table
          highlightOnHover
          fz="xs"
          styles={{
            td: { whiteSpace: 'nowrap' },
            th: { whiteSpace: 'nowrap' },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Phone</Table.Th>
              <Table.Th>Source</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Assignee</Table.Th>
              <Table.Th>Last contact</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data?.rows ?? []).map((r) => (
              <Table.Tr
                key={r.id}
                onClick={() => navigate(`~/leads/${r.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>
                  {[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}
                </Table.Td>
                <Table.Td>{r.email ?? '—'}</Table.Td>
                <Table.Td>{r.phone ?? '—'}</Table.Td>
                <Table.Td>{r.source}</Table.Td>
                <Table.Td>
                  <StatusBadge status={r.status} />
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <AssignAssigneeControl
                    leadId={r.id}
                    assigneeUserId={r.assigneeUserId}
                    assigneeLabel={r.assigneeInfo?.name || r.assigneeEmail}
                    assigneePicture={r.assigneeInfo?.picture}
                    readOnly={!canAssign}
                  />
                </Table.Td>
                <Table.Td>
                  {r.lastContactAt ? formatDate(r.lastContactAt) : '—'}
                </Table.Td>
                <Table.Td>{formatDate(r.createdAt)}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && (data?.rows ?? []).length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={9} align="center">
                  No leads
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        <Group justify="flex-end">
          <Pagination
            total={Math.max(1, Math.ceil((data?.total ?? 0) / state.pageSize))}
            value={state.page}
            onChange={(page) => setState({ page })}
          />
        </Group>

        <CreateLeadModal
          opened={createOpen}
          onClose={() => setCreateOpen(false)}
        />

        <ImportMondayLeadModal
          opened={importOpen}
          onClose={() => setImportOpen(false)}
        />
      </Stack>
    </Paper>
  );
}
